/**
 * The right panel's tab strip: one tab per open surface, plus the menu that
 * opens another.
 *
 * Extracted from `RightSidebarShell` so the shell stays chrome and slot while
 * the strip owns tab listing. The single tab's own interaction (context menu,
 * rename, mute) lives beside it in `rightSidebarSurfaceTab`. Rename and mute
 * port t3code's RightPanelTabs capabilities: rename lets the user name a tab
 * in place, and mute addresses the desktop browser view behind a preview
 * tab, offered only where that bridge exists.
 */
import { PlusIcon, SquarePlusIcon } from "lucide-react";

import type { RightPanelKind } from "../rightPanelSurface";

import { RIGHT_SIDEBAR_TAB_ICON as TAB_ICON } from "./RightSidebarShell.icons";
import type { AddableRightPanelKind, RightPanelSurfaceTab } from "./rightPanelSurfaceTabs";
import { SurfaceTab } from "./RightSidebarSurfaceTabItem";
import { Menu, MenuItem, MenuPopup, MenuShortcut, MenuTrigger } from "./ui/menu";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";

export interface RightPanelPreviewAudio {
  readonly mutedSurfaceIds: ReadonlySet<string>;
  readonly onToggleMute: (surfaceId: string) => void;
}

export interface SurfaceTabStripProps {
  readonly tabs: ReadonlyArray<RightPanelSurfaceTab>;
  readonly onSelect: (surfaceId: string) => void;
  readonly onClose: (surfaceId: string) => void;
  /**
   * Name a tab in place; a blank label clears the name back to the derived
   * one. Absent where nothing can be renamed, which also removes the menu
   * item: an inert Rename is worse than none.
   */
  readonly onRenameSurface?: ((surfaceId: string, label: string) => void) | undefined;
  /** Mute controls for preview tabs; absent where there is nothing to mute. */
  readonly previewAudio?: RightPanelPreviewAudio | undefined;
}

/**
 * The row of surface tabs across the top of the right panel.
 */
export function SurfaceTabStrip(props: SurfaceTabStripProps) {
  const { tabs, onSelect, onClose, onRenameSurface, previewAudio } = props;

  return (
    <div
      className="flex min-w-0 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      aria-label="Right sidebar surfaces"
      data-testid="right-sidebar-surface-tabs"
    >
      {tabs.map((tab) => (
        <SurfaceTab
          key={tab.id}
          tab={tab}
          tabs={tabs}
          onSelect={onSelect}
          onClose={onClose}
          onRenameSurface={onRenameSurface}
          previewAudio={previewAudio}
        />
      ))}
    </div>
  );
}

interface AddSurfaceMenuProps {
  readonly kinds: ReadonlyArray<AddableRightPanelKind>;
  readonly onSelect: (kind: RightPanelKind) => void;
}

/**
 * The plus button and its menu of the surface kinds the thread can open.
 */
export function AddSurfaceMenu(props: AddSurfaceMenuProps) {
  const { kinds, onSelect } = props;

  return (
    <Menu>
      <Tooltip>
        <TooltipTrigger
          render={
            <MenuTrigger
              render={
                <button
                  type="button"
                  aria-label="Open another sidebar surface"
                  data-testid="right-sidebar-add-surface-trigger"
                  className="inline-flex size-11 shrink-0 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:size-9"
                >
                  <PlusIcon aria-hidden="true" className="size-4" />
                </button>
              }
            />
          }
        />
        <TooltipPopup>Add surface</TooltipPopup>
      </Tooltip>
      <MenuPopup align="start" className="min-w-[224px]">
        {kinds.map((kind) => (
          <AddSurfaceMenuItem key={kind.kind} kind={kind} onSelect={onSelect} />
        ))}
      </MenuPopup>
    </Menu>
  );
}

/**
 * The plus-menu row has no close affordance, because a surface that is not
 * open cannot be closed. It takes its own props rather than borrowing the
 * single-tab props and leaving `onClose` permanently unpassed.
 */
interface AddSurfaceMenuItemProps {
  readonly kind: AddableRightPanelKind;
  readonly onSelect: (kind: RightPanelKind) => void;
}

/**
 * One add-surface row: icon, label, and its status or shortcut.
 */
function AddSurfaceMenuItem(props: AddSurfaceMenuItemProps) {
  const { kind: tab, onSelect } = props;
  const Icon = TAB_ICON[tab.kind];

  return (
    <MenuItem
      disabled={tab.disabled}
      onClick={tab.disabled ? undefined : () => onSelect(tab.kind)}
      data-testid={`right-sidebar-add-surface-item-${tab.kind}`}
    >
      {tab.kind === "canvas" ? (
        <SquarePlusIcon aria-hidden="true" className="size-3.5 text-muted-foreground" />
      ) : (
        <Icon aria-hidden="true" className="size-3.5 text-muted-foreground" />
      )}
      <span className="min-w-0 flex-1 truncate">{tab.label}</span>
      {tab.statusLabel ? (
        <span className="rounded-full border border-border/60 px-1.5 py-0.5 text-[10px] text-muted-foreground">
          {tab.statusLabel}
        </span>
      ) : tab.shortcutLabel ? (
        <MenuShortcut>{tab.shortcutLabel}</MenuShortcut>
      ) : null}
    </MenuItem>
  );
}
